import { useEffect, useState } from 'react';
import {
  DELAIS_PROPOSES,
  acompteParDefaut,
  delaiMaison,
  libelleDelai,
  remiseParDefaut,
  VALIDITES_PROPOSEES,
  validiteParDefaut,
} from '../../lib/echeances';
import { TYPES_TVA, ecritTaux, tauxTvaEnVigueur, typeTvaDefaut } from '../../lib/tva';
import { C } from '../../theme/palette';
import { tr } from '../../lib/i18n';

const titre = {
  fontSize: 9,
  fontWeight: 700,
  color: C.muted,
  marginBottom: 5,
  textTransform: 'uppercase',
};
const aide = { fontSize: 8, color: C.muted, lineHeight: 1.5 };
const champ = {
  textAlign: 'center',
  fontWeight: 800,
  fontSize: 15,
  border: '1px solid ' + C.border,
  borderRadius: 9,
  padding: 9,
  color: C.ink,
};

/** Nombre décimal saisi à la suisse ou à la française (« 8,1 »). */
const nombreSaisi = (texte) => {
  const n = Number(String(texte).replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/**
 * Champ de pourcentage qui garde le texte tapé : un champ lié au seul nombre
 * avalait la virgule de « 8,1 » dès la première frappe.
 */
function ChampPourcent({ libelle, valeur, surValeur, largeur, placeholder }) {
  const [texte, setTexte] = useState(String(valeur));
  // Les réglages arrivent du serveur après l'ouverture de l'écran : le champ
  // affichait la valeur par défaut au lieu de celle enregistrée.
  useEffect(() => {
    if (nombreSaisi(texte) !== (Number(valeur) || 0)) setTexte(String(valeur));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valeur]);
  return (
    <input
      aria-label={libelle}
      value={texte}
      inputMode="decimal"
      placeholder={placeholder}
      onChange={(e) => {
        setTexte(e.target.value);
        surValeur(nombreSaisi(e.target.value));
      }}
      style={{ ...champ, width: largeur }}
    />
  );
}

/** Libellé court d'un type de taux : « Normal », « Réduit », « Hébergement ». */
const COURT = { normal: 'Normal', reduit: 'Réduit', hebergement: 'Hébergement' };

/**
 * Valeurs par défaut des nouveaux documents, en tête de la carte « Modèle des
 * documents » comme dans la maquette V288 : délai de paiement, validité et
 * acompte des offres, et TVA.
 *
 * Elles se rangent dans les réglages de l'entreprise, donc sur le serveur, et
 * les écrans de création les lisent (`lib/echeances.js`, `lib/tva.js`) : une
 * offre ou une facture neuve les reprend, un document existant garde les
 * siennes.
 *
 * La TVA par défaut se choisit parmi les trois taux légaux — ou 0 % pour une
 * entreprise non assujettie — et non plus en nombre libre : c'est le type qui
 * est retenu, si bien qu'un changement de taux légal (carte « Taux de TVA »)
 * suit de lui-même.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.soc entreprise
 * @param {(patch: object) => void} props.set applique une modification à
 *   l'entreprise
 * @returns {import('react').ReactElement} les trois réglages
 */
export function DefautsDocuments({ soc, set }) {
  const delai = delaiMaison(soc);
  const taux = tauxTvaEnVigueur(soc);
  const typeTva = typeTvaDefaut(soc);
  const choixTva = [
    ...TYPES_TVA.map((t) => ({
      cle: t.cle,
      libelle: `${tr(COURT[t.cle])} ${ecritTaux(taux[t.cle])}`,
    })),
    { cle: 'exonere', libelle: `${ecritTaux(0)} · non assujetti` },
  ];
  return (
    <div style={{ marginBottom: 13 }}>
      <div style={titre}>Délai de paiement par défaut</div>
      <div
        role="group"
        aria-label="Délai de paiement par défaut"
        style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 6 }}
      >
        {DELAIS_PROPOSES.map((jours) => {
          const on = delai === jours;
          return (
            <button
              key={jours}
              type="button"
              aria-pressed={on}
              onClick={() => set({ delaiPaiementDefaut: jours })}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
                background: on ? C.navy : C.white,
                color: on ? '#fff' : C.grey,
                border: '1px solid ' + C.border,
              }}
            >
              {libelleDelai(jours)}
            </button>
          );
        })}
      </div>
      <div style={{ ...aide, marginBottom: 13 }}>
        Appliqué aux nouvelles factures. Un client peut avoir son propre délai (% Remises → fiche
        client).
      </div>

      <div style={titre}>Validité des offres</div>
      <div
        role="group"
        aria-label="Validité des offres"
        style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}
      >
        {VALIDITES_PROPOSEES.map((jours) => {
          const on = validiteParDefaut(soc) === jours;
          return (
            <button
              key={jours}
              type="button"
              aria-pressed={on}
              onClick={() => set({ validiteOffreDefaut: jours })}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
                background: on ? C.navy : C.white,
                color: on ? '#fff' : C.grey,
                border: '1px solid ' + C.border,
              }}
            >
              {libelleDelai(jours)}
            </button>
          );
        })}
        <ChampPourcent
          libelle="Validité des offres en jours"
          valeur={
            VALIDITES_PROPOSEES.includes(validiteParDefaut(soc)) ? '' : validiteParDefaut(soc)
          }
          surValeur={(n) => set({ validiteOffreDefaut: n > 0 ? Math.round(n) : '' })}
          largeur={64}
          placeholder="…"
        />
        <span style={aide}>jours</span>
      </div>
      <div style={{ ...aide, marginBottom: 13 }}>
        Combien de temps une nouvelle offre reste valable : « Valable jusqu’au » sur l’offre.
      </div>

      <label style={{ display: 'block' }}>
        <div style={titre}>Acompte demandé par défaut</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 13 }}>
          <ChampPourcent
            libelle="Acompte demandé par défaut"
            valeur={acompteParDefaut(soc) || ''}
            surValeur={(n) => set({ acompteDefaut: Math.min(100, n) })}
            largeur={70}
            placeholder="0"
          />
          <span style={{ fontSize: 13, fontWeight: 800, color: C.grey }}>%</span>
          <span style={aide}>sur les offres</span>
        </div>
      </label>

      <label style={{ display: 'block' }}>
        <div style={titre}>Remise globale par défaut</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 13 }}>
          <ChampPourcent
            libelle="Remise globale par défaut"
            valeur={remiseParDefaut(soc) || ''}
            surValeur={(n) => set({ remiseDefaut: Math.min(100, n) })}
            largeur={70}
            placeholder="0"
          />
          <span style={{ fontSize: 13, fontWeight: 800, color: C.grey }}>%</span>
          <span style={aide}>sur les offres · vide = aucune remise</span>
        </div>
      </label>

      <div style={titre}>TVA par défaut</div>
      <div
        role="group"
        aria-label="TVA par défaut"
        style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 6 }}
      >
        {choixTva.map((c) => {
          const on = typeTva === c.cle;
          return (
            <button
              key={c.cle}
              type="button"
              aria-pressed={on}
              onClick={() => set({ tvaDefaut: c.cle })}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
                background: on ? C.navy : C.white,
                color: on ? '#fff' : C.grey,
                border: '1px solid ' + C.border,
              }}
            >
              {c.libelle}
            </button>
          );
        })}
      </div>
      <div style={{ ...aide, marginBottom: 13 }}>
        Taux en vigueur aujourd'hui, réglés dans la carte « Taux de TVA » ci-dessous. Chaque
        document peut en choisir un autre, et chaque ligne porter le sien.
      </div>
      <div style={aide}>
        Appliqués automatiquement — plus besoin de les choisir dans chaque offre ou facture.
      </div>
    </div>
  );
}
